import React from 'react';
import PropTypes from 'prop-types';
import UploadIcon from '@material-ui/icons/CloudUpload';
import Dropzone from 'react-dropzone';
import config from '../../../../../config';
import TranslateProperty from '../../../../../components/TranslateProperty';
import '../chat-css/MessageInput.css';
import uploadFile, {getFileType} from '../../../../../utils/FileService';
import EmojiIcon from '../../../../../assets/images/emoji-icon.png';
import LockIcon from '../../../../../assets/images/lock.png';
import UnlockIcon from '../../../../../assets/images/unlock.png';
import AttachIcon from '../../../../../assets/images/attach-icon.png';
import TemplatesIcon from '../../../../../assets/images/templates-icon.png';
import TemplatesModal from "./TemplatesModal";
import {registerHotkeys} from "../../../../../utils/hotkeyListener";
import {emojis} from "../../../../../utils/Emoji";
import {myDarkBlue} from "../../../../../utils/General";
import CloseIcon from "../../../../../assets/images/close.png";
import Translate from "../../../../../components/Translate";
import Reply from "../../../../../assets/images/reply.png";
import {sendTypingMessage} from "../../../../../services/chat/api";

class MessageInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            dragIsActive: false,
            messageText: this.getStoredMessageText(),
            showEmojis: false,
            commentMessage: false,
            pendingFiles: [],
        };
        this.emojiInputRef = React.createRef();
        this.textareaRef = React.createRef();

        this.typingTimeout = null;
        this.isTyping = false;
    }

    componentDidMount() {
        this.focusTextareaToEnd();

        const {hotkeys} = this.props;

        this.cleanupHotkeys = registerHotkeys(hotkeys, {
            OPEN_TEMPLATES_MODAL: () => this.handleTemplatesClick(),
            OPEN_EMOJI_MODAL: () => this.toggleEmojis(),
            SWITCH_PRIVATE_MESSAGE_MODE: () => this.handleCommentMessageChange(),
        });
    }

    componentWillUnmount() {
        if (this.cleanupHotkeys) {
            this.cleanupHotkeys();
        }
    }

    notifyTypingStart = () => {
        const {conversation} = this.props;
        const {commentMessage} = this.state;

        if (!commentMessage) {
            if (!this.isTyping) {
                this.isTyping = true;
                sendTypingMessage(conversation.id, "start")
            }
            clearTimeout(this.typingTimeout);
            this.typingTimeout = setTimeout(() => {
                this.notifyTypingStop();
            }, 3000);
        }

    };

    notifyTypingStop = () => {
        const {conversation} = this.props;

        if (this.isTyping) {
            this.isTyping = false;
            sendTypingMessage(conversation.id, "stop")
        }
        clearTimeout(this.typingTimeout);
    };

    handleChange = (event) => {
        const messageText = event.target.value;
        const trimmedText = messageText.trim().toLowerCase();

        this.setState({messageText}, () => {
            this.storeMessageText(messageText);
        });

        this.notifyTypingStart();

        if (trimmedText.length > 1) {
            const {templates} = this.props;

            const filteredTemplates = templates.filter(t => {
                const text = t.text.toLowerCase();
                const folder = (t.folderName || '').toLowerCase();
                return text.includes(trimmedText) || folder.includes(trimmedText);
            });

            this.props.setSugTemplates(filteredTemplates);
        } else {
            this.props.setSugTemplates([]);
        }
    };

    getStoredMessageText = () => {
        const {conversation} = this.props;
        const storedMessageText = sessionStorage.getItem(conversation.id);
        return storedMessageText ? storedMessageText : '';
    };

    storeMessageText = (text) => {
        const {conversation} = this.props;
        sessionStorage.setItem(conversation.id, text);
    };

    toggleEmojis = () => {
        this.setState((prevState) => ({
            showEmojis: !prevState.showEmojis
        }), () => {
            if (this.state.showEmojis) {
                document.addEventListener('click', this.handleClickOutside);
            } else {
                document.removeEventListener('click', this.handleClickOutside);
            }
        });
    };

    addEmojiToTextarea = (emoji) => {
        this.setState((prevState) => ({
            messageText: prevState.messageText + emoji
        }), () => {
            this.storeMessageText(this.state.messageText);
        });
    };

    handleClickOutside = (event) => {
        if (this.emojiInputRef.current && !this.emojiInputRef.current.contains(event.target) && event.target.id !== 'emojiButton') {
            this.setState({showEmojis: false});
            document.removeEventListener('click', this.handleClickOutside);
        }
    };

    selectTemplate = (templateText) => {
        this.setState((prevState) => {
            const newText = templateText;
            this.storeMessageText(newText);
            this.props.setSugTemplates([]);
            return {messageText: newText};
        }, this.focusTextareaToEnd);
    };

    changeMessageTextToTemplate = (templateText) => {
        this.setState((prevState) => {
            const newText = prevState.messageText + templateText;
            return {messageText: newText};
        }, this.focusTextareaToEnd);
    };

    onDragEnter = () => {
        this.setState({
            dragIsActive: true,
        });
    };

    onDragLeave = () => {
        this.setState({
            dragIsActive: false,
        });
    };

    handleKeyPress = (event) => {
        if (event.charCode === 13 && !event.shiftKey) {
            event.preventDefault();
            this.sendTextMessage();
        }
    };

    sendTextMessage = () => {
        const {sendChatMessage, replyTo, setReplyTo} = this.props;
        const {messageText, commentMessage, pendingFiles} = this.state;

        const parentId = (replyTo && replyTo.id) || null;

        if (messageText.trim()) {
            const message = {
                type: config.messageTypes.text,
                text: messageText,
                parentId
            };
            sendChatMessage(message, commentMessage);
        }

        pendingFiles.forEach(file => {
            const attachmentMessage = {
                url: file.url,
                type: file.type,
                name: file.name,
                parentId
            };
            sendChatMessage(attachmentMessage);
        });

        this.setState({
            messageText: '',
            pendingFiles: [],
        });
        this.storeMessageText('');
        this.props.setSugTemplates([]);
        setReplyTo(null);

        if (commentMessage) {
            this.notifyTypingStop();
        } else {
            this.isTyping = false;
        }
    };

    uploadAttachment = (files) => {
        const data = new FormData();
        data.append('file', files[0]);

        const {addNotification} = this.props;

        this.setState({dragIsActive: false});

        uploadFile(data)
            .then((link) => {
                const file = {
                    url: link,
                    type: getFileType(files[0].type),
                    name: files[0].name,
                };

                this.setState((prevState) => ({
                    pendingFiles: [...prevState.pendingFiles, file]
                }));
                this.focusTextareaToEnd();
            })
            .catch((error) => {
                if (error.response && error.response.status === 413) {
                    addNotification('fileLarge', config.notificationLevels.error);
                } else {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                }
            });
    };

    handleTemplatesClick = () => {
        const {confirmAction, templates, user} = this.props;
        confirmAction({
            content: <TemplatesModal templates={templates}
                                     user={user}
                                     changeMessageTextToTemplate={this.changeMessageTextToTemplate}/>
        })
    };

    handleCommentMessageChange = () => {
        this.setState((prevState) => ({
            commentMessage: !prevState.commentMessage
        }));
    };

    focusTextareaToEnd = () => {
        const textarea = this.textareaRef.current;
        if (textarea) {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
        }
    };

    removePendingFile = (indexToRemove) => {
        this.setState((prevState) => ({
            pendingFiles: prevState.pendingFiles.filter((_, index) => index !== indexToRemove)
        }));
    };

    render() {
        const {dragIsActive, commentMessage, messageText, pendingFiles} = this.state;
        const {tariff, user, replyTo, setReplyTo} = this.props;
        const {features} = tariff;

        return (
            <Dropzone
                disableClick
                className="ignore"
                multiple={false}
                onDragEnter={this.onDragEnter}
                onDragLeave={this.onDragLeave}
                onDrop={this.uploadAttachment}
                style={{
                    position: 'relative',
                    height: '150px',
                }}
            >
                {dragIsActive ? (
                    <div className="centered-flex-container full-height" style={{backgroundColor: '#fff'}}>
                        <UploadIcon style={{fontSize: 72}}/>
                    </div>
                ) : (
                    <div className="message-input">

                        {this.state.showEmojis && (
                            <div className="emojiInput" id="emojiInput" ref={this.emojiInputRef}>
                                {emojis.map((item, index) => (
                                    <span
                                        key={index}
                                        className="emojiItem"
                                        onClick={() => this.addEmojiToTextarea(item.emoji)}
                                    >
                                      {item.emoji}
                                    </span>
                                ))}
                            </div>

                        )}

                        <div style={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px'
                        }}>
                            {replyTo && (
                                <div
                                    className="input-reply-preview"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: '#fff',
                                        borderLeft: '4px solid ' + myDarkBlue,
                                        borderRadius: '6px',
                                        marginRight: '10px',
                                        padding: '8px 12px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                        height: '36px',
                                        marginBottom: '4px',
                                        gap: '7px'
                                    }}
                                >
                                    <img src={Reply} width={13} height={13}/>

                                    <div style={{flex: 1, overflow: 'hidden'}}>
                                        <div
                                            style={{
                                                color: '#333',
                                                fontSize: '13px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '200px',
                                            }}
                                        >
                                            {['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(replyTo.type)
                                                ? <Translate text="attachment"/>
                                                : replyTo.text || ''}
                                        </div>
                                    </div>
                                    <div onClick={() => {
                                        setReplyTo(null);
                                        this.focusTextareaToEnd();
                                    }}
                                         style={{marginLeft: 8, cursor: 'pointer', fontSize: '16px'}}>
                                        <img src={CloseIcon} width={8} height={8}/>
                                    </div>
                                </div>
                            )}
                            {this.state.pendingFiles.length > 0 && (
                                <div
                                    className="pending-attachments"
                                    style={{
                                        marginRight: '5px',
                                        maxHeight: '60px',
                                        overflowY: 'auto',
                                        width: '165px',
                                    }}
                                >
                                    {this.state.pendingFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '4px',
                                                background: '#f5f5f5',
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                width: '100%',
                                            }}
                                        >
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flexGrow: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                minWidth: 0,
                                            }}
                                        >
                                            <img src={AttachIcon} width={10} height={10}/>
                                            <span style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {file.name}
                                            </span>
                                        </span>
                                            <button
                                                className="search-clear"
                                                style={{color: 'red', flexShrink: 0}}
                                                onClick={() => {
                                                    this.removePendingFile(index);
                                                    this.focusTextareaToEnd();
                                                }}
                                            >
                                                <img src={CloseIcon} width={8} height={8}/>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                                <TranslateProperty properties={[{
                                    key: 'placeholder',
                                    value: 'pleaseFillMessage2'
                                }]}>
                            <textarea
                                id="chat-message-input"
                                ref={this.textareaRef}
                                className="message-input-text"
                                value={messageText}
                                onKeyPress={this.handleKeyPress}
                                onChange={this.handleChange}
                                style={{marginLeft: '10px', height: '95%'}}
                            />
                                </TranslateProperty>
                            </div>
                        </div>


                        <div style={{display: 'flex', width: '100%', justifyContent: 'space-between', padding: '10px'}}>

                            <div style={{display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center'}}>

                                <div
                                    style={{
                                        backgroundColor: 'rgb(235, 236, 240)',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '5px',
                                        padding: '10px',
                                    }}
                                >
                                    {commentMessage ? (
                                        <div onClick={this.handleCommentMessageChange}>
                                            <img width={15} height={15} src={LockIcon}/>
                                        </div>
                                    ) : (
                                        <div onClick={this.handleCommentMessageChange}>
                                            <img width={15} height={15} src={UnlockIcon}/>
                                        </div>
                                    )}
                                </div>

                                <div style={{
                                    backgroundColor: 'rgb(235, 236, 240)',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '5px',
                                    padding: '10px'
                                }}>
                                    <div onClick={this.toggleEmojis}>
                                        <img width={15} height={15} src={EmojiIcon}/>
                                    </div>
                                </div>

                                {features.includes(config.features.FILE_SHARING) && (
                                    <div style={{
                                        backgroundColor: 'rgb(235, 236, 240)',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '5px',
                                        padding: '10px'
                                    }}>
                                        <div className="message-input-controls">
                                            <Dropzone
                                                className="ignore centered-flex-container input-control attachment-button-tooltip"
                                                multiple={false} onDrop={this.uploadAttachment}>
                                                <img width={15} height={15} src={AttachIcon}/>
                                            </Dropzone>
                                        </div>
                                    </div>
                                )}

                                {features.includes(config.features.TEMPLATES) && (
                                    <div style={{
                                        backgroundColor: 'rgb(235, 236, 240)',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '5px',
                                        padding: '10px'
                                    }}>

                                        <div onClick={this.handleTemplatesClick}>
                                            <img width={15} height={15} src={TemplatesIcon}/>
                                        </div>
                                    </div>
                                )}
                            </div>


                            <div style={{
                                backgroundColor: 'rgb(4, 62, 92)',
                                borderRadius: '50%',
                            }}>
                                <button
                                    disabled={!messageText.trim() && pendingFiles.length === 0}
                                    type="button"
                                    style={{
                                        height: '40px',
                                        width: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: (!messageText.trim() && pendingFiles.length === 0) ? '#ccc' : '#043e5c',
                                        cursor: (!messageText.trim() && pendingFiles.length === 0) ? 'not-allowed' : 'pointer',
                                        opacity: (!messageText.trim() && pendingFiles.length === 0) ? 0.6 : 1,
                                        transition: 'all 0.3s ease'
                                    }}
                                    className="no-style-button input-control"
                                    onClick={this.sendTextMessage}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="1.8em" height="1.8em"
                                         viewBox="0 0 24 24">
                                        <path fill="white" d="M3 20V4l19 8zm2-3l11.85-5L5 7v3.5l6 1.5l-6 1.5zm0 0V7z"/>
                                    </svg>
                                </button>
                            </div>

                        </div>


                    </div>
                )}
            </Dropzone>
        );
    }
}

MessageInput.propTypes = {
    sendChatMessage: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    conversation: PropTypes.object.isRequired,
};

export default MessageInput;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/MessageInput.jsx